import React from "react";
import { Truck, Wrench, CreditCard, Moon, BadgeCheck, Headphones, Phone, MapPin } from "lucide-react";
import Seo, { breadcrumbJsonLd } from "@/components/Seo";
import { Breadcrumbs } from "@/components/common";
import { SERVICES, SITE } from "@/mock";

const ICONS = { Truck, Wrench, CreditCard, Moon, BadgeCheck, Headphones };

export default function Services() {
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Delivery & Services", path: "/services" },
  ];
  return (
    <>
      <Seo
        title="Delivery & Services in Fort Lauderdale"
        description="Local mattress delivery, professional set-up and old-mattress removal, special financing, a 60-day price guarantee and a 60-day comfort guarantee at MattressTown."
        path="/services"
        jsonLd={breadcrumbJsonLd(crumbs)}
      />
      <Breadcrumbs items={crumbs} />

      {/* Hero header */}
      <section className="relative overflow-hidden bg-neutral-900">
        <img
          src={SITE.storePhotos[1]}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-20"
        />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <span className="inline-block rounded-full bg-[#D0021B] px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-white">
            Rest Easy
          </span>
          <h1 className="mt-4 max-w-3xl text-4xl font-bold uppercase leading-tight text-white sm:text-5xl">
            Delivery &amp; Services
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-neutral-300">
            Buying a mattress should be effortless. From fast local delivery to expert
            guidance, MattressTown takes care of everything so you can rest easy.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon] || Truck;
            return (
              <div
                key={s.title}
                className="group relative overflow-hidden rounded-2xl border border-neutral-200 bg-white p-7 shadow-sm transition hover-lift"
                data-testid={`service-card-${i}`}
              >
                <span className="absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-[#D0021B] transition-transform duration-300 group-hover:scale-x-100" />
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#D0021B]/10 transition-colors group-hover:bg-[#D0021B]">
                  <Icon className="h-7 w-7 text-[#D0021B] transition-colors group-hover:text-white" aria-hidden="true" />
                </span>
                <h2 className="mt-5 text-lg font-bold text-neutral-900">{s.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-neutral-600">{s.desc}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-14 grid items-center gap-6 rounded-3xl bg-neutral-900 p-8 text-white sm:grid-cols-[1fr_auto] sm:p-10">
          <div>
            <h2 className="text-2xl font-bold uppercase sm:text-3xl">Questions About Delivery?</h2>
            <p className="mt-2 text-neutral-300">Call our team or visit the showroom - we're happy to help.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href={SITE.phoneHref} data-testid="services-call-btn" className="inline-flex items-center gap-2 rounded-full bg-[#D0021B] px-6 py-3 text-sm font-bold uppercase tracking-wide text-white hover:bg-[#a80016]"><Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phone}</a>
            <a href={SITE.address.mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="services-visit-btn" className="inline-flex items-center gap-2 rounded-full border-2 border-white px-6 py-3 text-sm font-bold uppercase tracking-wide text-white hover:bg-white hover:text-neutral-900"><MapPin className="h-4 w-4" aria-hidden="true" /> Visit Us</a>
          </div>
        </div>
      </div>
    </>
  );
}
